import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Card, Button, Badge, SearchField, type SearchFieldProps } from '@/components/ui';
import { useDeviceSize } from '../hooks/useDeviceSize';
import { useTabletLayout, COMPACT_FILTERS_QUERY } from '../hooks/useTabletLayout';

interface FiltersCardProps {
  title?: string;
  subtitle?: string;
  /** When false, subtitle is hidden (e.g. tablet compact mode). */
  showSubtitle?: boolean;
  children: ReactNode;
  className?: string;
  /** Applied to the card body wrapper (e.g. scrollable region on tablet). */
  bodyClassName?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /**
   * Start collapsed on a phone (< md), regardless of `defaultCollapsed`.
   *
   * Measured on a 414 x 896 viewport: an expanded filter card pushed the first row of data to
   * y=1153 on Work IDs, y=964 on Quantified and y=740 on Routine — so the user scrolled past a
   * screenful of filters before seeing a single record. That is the "a lot of top nav items and
   * filters, I cannot see items at all" report. Every caller passes `defaultCollapsed={false}`,
   * which was written with a desktop in mind, so this deliberately overrides it on phones only.
   *
   * Requires `collapsible` — there is no point starting collapsed with no way to expand.
   */
  collapseOnPhone?: boolean;
  /**
   * Start collapsed on a tablet (an iPad in either orientation, or any touch-first screen) and on
   * a short laptop screen (<= 900px tall) - see `COMPACT_FILTERS_QUERY` - regardless of `defaultCollapsed` (W-iP6). Expanded filter panels took
   * 30-40% of an 820px-tall iPad, leaving 2-5 table rows. The collapsed bar keeps the title, the
   * chevron and a summary chip ("3 filters applied"), and a tap anywhere on it expands it, so it
   * stays obvious where the filters are. Requires `collapsible`.
   */
  collapseOnTablet?: boolean;
  /**
   * How many filters differ from their defaults, for the collapsed bar's summary chip. Without it
   * the chip falls back to "Filters applied" / "No filters applied" from `hasActiveFilters`.
   */
  activeFilterCount?: number;
  onToggleCollapse?: (collapsed: boolean) => void;
  /** Defaults to true when `onApply` is given: every list page has the same Apply button. */
  showApplyButton?: boolean;
  onApply?: () => void;
  /**
   * Pass `!hasUnappliedChanges`: Apply is a solid primary button while there are changes to
   * apply and a plainly disabled one when there are none (W-M1).
   */
  applyDisabled?: boolean;
  /** Kept for compatibility; the label is "Apply" on every page (W-M1). */
  applyButtonText?: string;
  /** Defaults to true when `onClear` is given. Clear All is always shown, disabled at defaults. */
  showClearButton?: boolean;
  onClear?: () => void;
  clearButtonText?: string;
  /**
   * True only when a filter differs from its DEFAULT (not merely when a value is set): colours
   * the collapsed summary chip and enables Clear All.
   */
  hasActiveFilters?: boolean;
  /**
   * V2-076 / FLT-05: every filter that currently hides records, INCLUDING defaults (Display
   * Inactive off, Status = Active). The chip then reads "N filters applied" – grey when only the
   * defaults apply, amber once the user has changed something – and never "No filters applied"
   * while a default hides records. Takes precedence over `activeFilterCount` for the chip text.
   */
  appliedFilterCount?: number;
  /**
   * V2-077 / SRCH-01: the page's search, always visible in this header bar next to the title, so
   * it stays on screen when the panel is collapsed. Replaces any header Search button. Pass the
   * SearchField props, or a ready-made field element (Equipment's FilterSearchField).
   */
  search?: SearchFieldProps | ReactNode;
}

const FiltersCard: React.FC<FiltersCardProps> = ({
  title = 'Filters',
  subtitle,
  showSubtitle = true,
  children,
  className = '',
  bodyClassName = '',
  collapsible = false,
  defaultCollapsed = false,
  collapseOnPhone = true,
  collapseOnTablet = true,
  activeFilterCount,
  onToggleCollapse,
  showApplyButton,
  onApply,
  applyDisabled = false,
  applyButtonText: _applyButtonText,
  showClearButton,
  onClear,
  clearButtonText = 'Clear All',
  hasActiveFilters = false,
  appliedFilterCount,
  search,
}) => {
  const showApply = showApplyButton ?? Boolean(onApply);
  const showClear = showClearButton ?? Boolean(onClear);
  const { isMobile } = useDeviceSize();
  // Tablet, or a short laptop screen (<= 900px tall) - see COMPACT_FILTERS_QUERY.
  const isTablet = useTabletLayout(COMPACT_FILTERS_QUERY);
  const startCollapsedOnPhone = collapsible && collapseOnPhone;
  const startCollapsedOnTablet = collapsible && collapseOnTablet;

  const [isCollapsed, setIsCollapsed] = useState(
    () => defaultCollapsed || (startCollapsedOnPhone && isMobile) || (startCollapsedOnTablet && isTablet)
  );
  const previousDefaultCollapsed = useRef(defaultCollapsed);
  const wasMobile = useRef(isMobile);
  const wasTablet = useRef(isTablet);

  useEffect(() => {
    if (previousDefaultCollapsed.current !== defaultCollapsed) {
      setIsCollapsed(defaultCollapsed);
      previousDefaultCollapsed.current = defaultCollapsed;
    }
  }, [defaultCollapsed]);

  // Collapse when the viewport crosses INTO phone width (rotating a tablet to portrait, or a
  // desktop window being narrowed). Only on the crossing, so a deliberate expand is not stomped
  // on every resize event; and never on the way back out, where there is room to show them.
  useEffect(() => {
    if (isMobile && !wasMobile.current && startCollapsedOnPhone) {
      setIsCollapsed(true);
    }
    wasMobile.current = isMobile;
  }, [isMobile, startCollapsedOnPhone]);

  // Same rule for the tablet query (the first paint can miss it: matchMedia is read in an effect).
  useEffect(() => {
    if (isTablet && !wasTablet.current && startCollapsedOnTablet) {
      setIsCollapsed(true);
    }
    wasTablet.current = isTablet;
  }, [isTablet, startCollapsedOnTablet]);

  const handleToggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    onToggleCollapse?.(newCollapsed);
  };

  // The whole header bar toggles the panel: a tap anywhere on it (title, chip, empty space,
  // chevron) expands or collapses. Clear All / Apply and any other control keep their own job.
  const handleHeaderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!collapsible) return;
    const target = e.target as Element;
    const control = target.closest('button, a, input, select, textarea, label, [role="button"]');
    if (control && !control.closest('[data-filters-toggle]')) return;
    handleToggleCollapse();
  };

  const chipCount = appliedFilterCount ?? activeFilterCount;
  const summaryText =
    chipCount !== undefined
      ? chipCount > 0
        ? `${chipCount} filter${chipCount === 1 ? '' : 's'} applied`
        : 'No filters applied'
      : hasActiveFilters
      ? 'Filters applied'
      : 'No filters applied';
  // Amber only when the user changed something; defaults alone read neutral grey (FLT-05).
  const chipChanged = appliedFilterCount !== undefined ? hasActiveFilters : hasActiveFilters || (activeFilterCount ?? 0) > 0;
  const showSummary = collapsible && isCollapsed;

  const TitleTag = collapsible ? 'button' : 'div';

  return (
    <Card className={`tw-mb-3 ${className}`}>
      <Card.Header
        className={`tw-py-2.5 ${collapsible ? 'tw-cursor-pointer' : ''}`}
        onClick={collapsible ? handleHeaderClick : undefined}
        data-collapsed={collapsible ? String(isCollapsed) : undefined}
      >
        {/* flex-wrap + a full-width title row below sm. With Clear All, Apply and the collapse
            toggle all in this header, three buttons plus their gaps consume ~300px of a 414px
            phone — measured, the title block was squeezed to ZERO width. Letting the buttons
            wrap to their own line gives the title its width back; above sm it is a single row. */}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-y-2 tw-gap-x-3">
          <TitleTag
            type={collapsible ? 'button' : undefined}
            data-filters-toggle={collapsible ? '' : undefined}
            aria-expanded={collapsible ? !isCollapsed : undefined}
            className={`tw-flex tw-min-w-0 tw-basis-full tw-items-center tw-gap-2.5 tw-bg-transparent tw-p-0 tw-text-left sm:tw-basis-0 sm:tw-grow ${
              collapsible ? 'tw-cursor-pointer' : ''
            }`}
            title={collapsible ? (isCollapsed ? 'Expand filters' : 'Collapse filters') : undefined}
          >
            <span className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-surface-alt tw-text-content-secondary">
              <i className="bi bi-funnel" aria-hidden="true"></i>
            </span>
            <span className="tw-min-w-0">
              <span className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-content">{title}</span>
                {/* Collapsed: say what is applied, so the bar still reads as "the filters". Expanded,
                    the fields show the values, so there is no "Active" pill (WRAP-2068: readers took
                    it for a task status). */}
                {showSummary && (
                  <Badge
                    pill
                    bg={chipChanged ? 'warning' : 'light'}
                    data-testid="filters-summary"
                    className="tw-whitespace-nowrap"
                  >
                    {summaryText}
                  </Badge>
                )}
              </span>
              {subtitle && showSubtitle && (
                // Wraps rather than truncating: help text cut off with "..." could not be read (W-iP11).
                <span className="tw-mt-0.5 tw-block tw-text-xs tw-text-content-muted">{subtitle}</span>
              )}
            </span>
          </TitleTag>
          {search &&
            (React.isValidElement(search) ? (
              <div className="tw-order-last tw-basis-full md:tw-order-none md:tw-basis-auto md:tw-w-80 lg:tw-w-96" data-slot="filters-search">
                {search}
              </div>
            ) : (
              <SearchField {...(search as SearchFieldProps)} />
            ))}
          <div className="tw-flex tw-shrink-0 tw-grow tw-items-center tw-justify-end tw-gap-2 sm:tw-grow-0">
            {/* Action buttons */}
            {/* The same two buttons on every list page (W-M1): Clear All (outline, disabled at
                the defaults) and Apply (solid primary while there are changes to apply). */}
            {showClear && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={onClear}
                disabled={!hasActiveFilters}
                title={hasActiveFilters ? 'Reset every filter to its default' : 'Filters are at their defaults'}
              >
                <i className="bi bi-x-lg tw-mr-1" aria-hidden="true"></i>
                {clearButtonText}
              </Button>
            )}
            {showApply && (
              <Button
                variant={applyDisabled ? 'light' : 'primary'}
                size="sm"
                onClick={onApply}
                disabled={applyDisabled}
                data-state={applyDisabled ? 'idle' : 'pending'}
                title={applyDisabled ? 'No filter changes to apply' : 'Apply the changed filters'}
                className={applyDisabled ? 'tw-border-line-input tw-text-content-muted' : ''}
              >
                <i className="bi bi-check-lg tw-mr-1" aria-hidden="true"></i>
                Apply
              </Button>
            )}
            {/* Collapse button */}
            {collapsible && (
              // No onClick of its own: the click bubbles to the header, which toggles.
              <Button
                variant="outline-secondary"
                size="sm"
                data-filters-toggle=""
                aria-expanded={!isCollapsed}
                aria-label={isCollapsed ? 'Expand filters' : 'Collapse filters'}
                title={isCollapsed ? "Expand filters" : "Collapse filters"}
              >
                <i className={`bi ${isCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`} aria-hidden="true"></i>
              </Button>
            )}
          </div>
        </div>
      </Card.Header>
      {(!collapsible || !isCollapsed) && (
        <Card.Body className={bodyClassName}>
          {children}
        </Card.Body>
      )}
    </Card>
  );
};

export default FiltersCard; 